import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import axios from "axios";
import { Menu, X, Lock } from "lucide-react";
import Maintenance from "./Maintenance";
import "../../styles/public.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV = [
  { to: "/dags-leje", label: "Dags leje" },
  { to: "/langtid-leje", label: "Langtid leje" },
  { to: "/til-salg", label: "Biler til salg" },
  { to: "/saelg-din-bil", label: "Sælg din bil" },
  { to: "/om-os", label: "Om os" },
  { to: "/kontakt", label: "Kontakt" },
];

export default function PublicLayout() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null); // null = still loading
  const loc = useLocation();
  const onHero = loc.pathname === "/" && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  useEffect(() => {
    axios.get(`${API}/public/site-info`).then((r) => setInfo(r.data)).catch(() => setInfo({}));
  }, []);

  // While site-info is loading, render nothing so we don't flash the public site
  // before knowing whether maintenance mode is on.
  if (info === null) {
    return <div className="min-h-screen bg-graphite" data-testid="public-loading"/>;
  }

  if (info.maintenance_mode) {
    return <Maintenance info={info} />;
  }

  return (
    <div className="public-page min-h-screen" style={{ colorScheme: "light" }}>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          onHero ? "bg-transparent" : "bg-[#F7F3EC]/90 backdrop-blur-md border-b border-[#E5DED1]"
        }`}
        data-testid="public-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 sm:h-20">
          <Link to="/" className="flex items-center" data-testid="public-logo-link">
            <img
              src="/brand/mahasa-logo.png"
              alt="Mahasa"
              className={`h-8 sm:h-10 md:h-11 w-auto logo-img ${onHero ? "logo-on-dark" : ""}`}
            />
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.exact}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm tracking-wide transition-colors ${
                    onHero ? "text-white/85 hover:text-white" : "text-graphite/75 hover:text-graphite"
                  } ${isActive ? "font-semibold" : "font-medium"}`
                }
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <button
            className={`md:hidden p-2 ${onHero ? "text-white" : "text-graphite"}`}
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {open && (
          <div className="md:hidden bg-[#F7F3EC] border-t border-[#E5DED1] px-4 py-6 space-y-2" data-testid="mobile-nav">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.exact}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-xl text-base ${
                    isActive ? "bg-white text-graphite font-semibold" : "text-graphite/80"
                  }`
                }
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </NavLink>
            ))}
          </div>
        )}
      </header>

      <main className="pt-16 sm:pt-20">
        <Outlet />
      </main>

      {info.show_admin_button !== false && (
        <Link
          to="/login"
          aria-label="Admin"
          title="Admin"
          className="fixed bottom-4 left-4 z-40 group flex items-center gap-2 px-3 py-2 rounded-full bg-graphite/70 hover:bg-graphite text-white/60 hover:text-white text-xs tracking-wide backdrop-blur border border-white/10 shadow-lg transition-all"
          data-testid="public-admin-shortcut"
        >
          <Lock size={13} strokeWidth={1.6} />
          <span className="hidden group-hover:inline-block whitespace-nowrap uppercase tracking-wider">Admin</span>
        </Link>
      )}

      <Footer info={info} />
    </div>
  );
}

function Footer({ info = {} }) {
  const email = info.contact_email || "kontakt@mahasa.dk";
  const phone = info.contact_phone || "";
  const cvr = info.cvr || "";
  return (
    <footer className="bg-graphite text-white/80 mt-20" data-testid="public-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <img src="/brand/mahasa-logo.png" alt="Mahasa" className="h-12 w-auto logo-on-dark mb-4" />
          <p className="font-body text-white/60 text-sm leading-relaxed max-w-md mb-4">
            Biludlejning på månedsabonnement og salg af kvalitetsbiler. Fleksible mobilitetsløsninger med fokus på gennemsigtighed, service og attraktive priser.
          </p>
          <div className="text-xs text-white/50 space-y-1">
            {cvr && <div data-testid="footer-cvr">CVR: {cvr}</div>}
            {phone && <div><a href={`tel:${phone}`} className="hover:text-white">{phone}</a></div>}
            {email && <div><a href={`mailto:${email}`} className="hover:text-white">{email}</a></div>}
          </div>
        </div>
        <div>
          <div className="label-tag mb-4 text-copper">Navigation</div>
          <ul className="space-y-2 text-sm">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link to={n.to} className="hover:text-white transition-colors">{n.label}</Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="label-tag mb-4 text-copper">Juridisk</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/privatlivspolitik" className="hover:text-white">Privatlivspolitik</Link></li>
            <li><Link to="/handelsbetingelser" className="hover:text-white">Handelsbetingelser</Link></li>
            <li><Link to="/kontakt" className="hover:text-white">Kontakt os</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-6 text-center text-xs text-white/40">
        © {new Date().getFullYear()} Mahasa · Alle rettigheder forbeholdt
      </div>
    </footer>
  );
}
